
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast'
import Loader from '../components/loader';
import { checkAuth } from '../utils/auth';
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);
const API = process.env.REACT_APP_API_URL

export const AuthProvider = ({ children }) => {
  // Auth states
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const token = localStorage.getItem("token");
    return !!token;
  });
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem("role") === "admin");
  const [authLoading, setAuthLoading] = useState(true);

  // User states
  const [user, setUser] = useState({ name: "", email: "", mobile: "", addresses: [] });

  // Cart & Wishlist states
  const [cart, setCart] = useState({});
  const [wishlist, setWishlist] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState([]);

  // --- NEW: Order History State (Step 1 Update) ---
  const [orders, setOrders] = useState([]);

  // Purchase states
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [purchaseSubtotal, setPurchaseSubtotal] = useState(0);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  // Products states
  const [products, setProducts] = useState([]);

  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const lastTokenRef = useRef(localStorage.getItem("token"));

  // Helper functions
  const isProfileComplete = (user) => {
    return user?.name && user?.email && user?.mobile && user?.addresses?.length > 0;
  };

  const handleBuyNow = (items, subtotal) => {
    if (!isLoggedIn) {
      toast.error("Please sign in to continue.");
      navigate("/auth");
      return;
    }
    setPurchaseItems(items);
    setPurchaseSubtotal(subtotal);
    setPurchaseModalOpen(true);
  };

  // --- NEW: Place Order Function (Updates Local State) ---
  const placeOrder = (cartItems, totalAmount, shippingAddress) => {
    const newOrder = {
      id: `ORD-${Math.floor(100000 + Math.random() * 900000)}`, // Temp ID until refresh
      date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      total: totalAmount,
      shipTo: typeof shippingAddress === 'object' ? shippingAddress.name : shippingAddress,
      status: "Processing",
      deliveryDate: "Estimated in 3 days",
      deliveryMessage: "We are packing your items",
      paymentStatus: "Paid",
      isArchived: false,
      items: cartItems.map((item) => ({
        id: item.product_id || item.id,
        name: item.name,
        qty: item.quantity || 1,
        price: item.price,
        returnWindow: "Return eligible for 15 days",
        img: item.image || item.image_url || '/placeholder.jpg'
      }))
    };

    // Update state immediately so OrdersPage shows it
    setOrders((prevOrders) => [newOrder, ...prevOrders]);
  };

  // --- NEW: Fetch Orders from Backend ---
  const fetchOrders = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API}/api/products/medical`);
      if (!res.ok) {
        throw new Error(`API returned status: ${res.status}`);
      }
      const data = await res.json();

      const mapped = data.map(p => ({
        ...p,
        id: p.product_id,
        smalldescription: p.short_description,
        fullDescription: p.full_description,
        inStock: p.in_stock,
        image: p.image_url,
      }));

      setProducts(mapped);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch wishlist
  const fetchWishlist = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/api/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setWishlist(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  // Fetch cart
  const fetchCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/api/cart`, {
        headers: { 'Authorization': `Bearer ${token} ` }
      });
      const data = await res.json();
      if (res.ok) {
        setCart(data);
      }
    } catch (err) {
      // console.error(err);
    } finally {
      setAuthLoading(false);
    }
  };

  // Sync auth from localStorage
  const syncAuthFromStorage = () => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    const storedUser = localStorage.getItem("user");

    const tokenChanged = token !== lastTokenRef.current;
    if (tokenChanged) lastTokenRef.current = token;

    setIsLoggedIn(!!token);
    setIsAdmin(role === "admin");

    if (token && storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser({
          name: parsed.name || "",
          email: parsed.email || "",
          mobile: parsed.mobile || "",
          addresses: parsed.addresses || []
        });
      } catch (e) {
        console.error("Failed to parse user", e);
        localStorage.removeItem("user");
        setUser({ name: "", email: "", mobile: "", addresses: [] });
      }
    } else {
      setUser({ name: "", email: "", mobile: "", addresses: [] });
      localStorage.removeItem("user");
    }

    return tokenChanged;
  };

  // Initial auth sync
  useEffect(() => {
    syncAuthFromStorage();
  }, []);

  // Fetch data after auth is synced
  useEffect(() => {
    if (isLoggedIn) {
      fetchWishlist();
      fetchCart();
      fetchOrders(); // <--- NEW: Fetch orders when logged in
    } else {
      setAuthLoading(false);
    }
    fetchProducts();
  }, [isLoggedIn]);

  const handleSessionExpired = () => {
    toast.error("Session expired. Please login to continue.");

    // Clean up localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user");

    // Update React state so header updates immediately
    setIsLoggedIn(false);
    setIsAdmin(false);
    setUser({ name: "", email: "", mobile: "", addresses: [] });
    setCart({});
    setWishlist([]);
    setOrders([]);
    lastTokenRef.current = null;

    navigate("/auth?tab=register");
  };
  // Listen for storage changes
  useEffect(() => {
    const handler = () => syncAuthFromStorage();
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      if (!checkAuth() && isLoggedIn) {
        handleSessionExpired();
      }
    }, 10000); // check every 10s

    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // Login handler
  const handleLoginSuccess = async (userData) => {
    const token = userData?.token;
    const userObj = userData?.user || userData;

    if (!token) {
      console.error("Login failed: token missing");
      return;
    }

    syncAuthFromStorage();
    setIsLoggedIn(true);
    if (userObj) setUser(userObj);

    fetchWishlist();
    fetchOrders(); // <--- NEW: Fetch orders on login

    // Merge guest cart
    const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
    if (guestCart.length > 0) {
      try {
        await fetch(`${API}/api/cart/merge`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(guestCart),
        });
        localStorage.removeItem("cart");
      } catch (err) {
        console.error("Failed to merge guest cart:", err);
        toast.error("Could not merge guest cart");
      }
    }

    lastTokenRef.current = token;
    navigate("/");
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user");
    setIsLoggedIn(false);
    setUser({ name: "", email: "", mobile: "", addresses: [] });
    setCart({});
    setWishlist([]);
    setOrders([]); // <--- NEW: Clear orders on logout
    lastTokenRef.current = null;
    navigate("/");
  };

  // Context value
  const value = {
    isLoggedIn,
    isAdmin,
    user,
    setUser,
    cart,
    setCart,
    wishlist,
    setWishlist,
    wishlistProducts,
    handleLoginSuccess,
    handleLogout,
    fetchWishlist,
    fetchCart,
    // --- NEW: Orders values ---
    orders,
    setOrders,
    placeOrder,
    fetchOrders,
    // --------------------------
    purchaseItems,
    syncAuthFromStorage,
    setPurchaseItems,
    purchaseSubtotal,
    setPurchaseSubtotal,
    purchaseModalOpen,
    setPurchaseModalOpen,
    handleBuyNow,
    fetchProducts,
    products,
    authLoading,
    handleIncreaseQuantity: (id) => setCart(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 })),
    handleDecreaseQuantity: (id) => setCart(prev => {
      const qty = (prev[id] || 0) - 1;
      if (qty <= 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: qty };
    }),
    handleAddToWishlist: async (product) => {
      if (!checkAuth()) {
        handleSessionExpired();
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API}/api/wishlist/add`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            product_id: product.product_id,
            category: product.category || product.subcategory || "education",
            name: product.name,
            price: product.price,
            image_url: product.image_url,
            in_stock: product.in_stock
          })
        });

        if (!res.ok) throw new Error("Add wishlist failed");

        setWishlist(prev => [...prev, product]); // store full product snapshot
        fetchWishlist();
      } catch (err) {
        console.error("Wishlist add error:", err);
      }
    },
    handleRemoveFromWishlist: async (id, category) => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API}/api/wishlist/remove`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ product_id: id, category: category })
        });
        if (!res.ok) throw new Error("Remove wishlist failed");
        setWishlist(prev => prev.filter(x => x !== id));
        fetchWishlist();
      } catch (err) {
        console.error("Wishlist remove error:", err);
      }
    },
    cartCount: Object.values(cart).reduce((a, b) => a + b, 0),
  };

  if (authLoading) {
    return <Loader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};